import React from 'react';

interface AssetContextValue {
  /**
   * Whether the asset has a context menu.
   *
   * @optional
   * @type boolean
   */
  hasContextMenu?: boolean;
  /**
   * Whether the score is still loading.
   * Image is available but score is still being calculated.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isScoreLoading?: boolean;
  /**
   * Whether the asset is broken.
   *
   * @optional
   * @type boolean
   */
  isBroken?: boolean;
  /**
   * Whether the asset is hovered (used for displaying the context menu).
   *
   * @optional
   * @type boolean
   * @default false
   */
  isHovered?: boolean;
  /**
   * Whether the asset is focused via keyboard (used for displaying the context menu on focus).
   *
   * @optional
   * @type boolean
   * @default false
   */
  isFocused?: boolean;
  /**
   * Whether keyboard (focus-visible) focus is inside the tile. Drives the
   * states that must not stick after a mouse click leaves a button focused:
   * the selected ring and the hover-action reveal.
   */
  isFocusVisible?: boolean;
  /**
   * Whether the asset is loading.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the asset is selected.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isSelected?: boolean;
  /**
   * Whether the asset is being uploaded.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isUploading?: boolean;
  /**
   * Whether the asset is unscored.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isUnscored?: boolean;
  /**
   * Label to display when the asset is unscored.
   *
   * @optional
   * @type {'Unscored' | 'Informational'}
   * @default 'Informational'
   */
  unscoredLabel?: 'Unscored' | 'Informational';
  /**
   * Whether the asset is AI-generated.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isAiGenerated?: boolean;
  /**
   * Number of processed images generated from this asset.
   *
   * @optional
   * @type number
   */
  generatedChildCount?: number;
  /**
   * Whether the asset is in view-only mode.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isViewOnly?: boolean;
  /**
   * Score of asset.
   *
   * @optional
   * @type number
   */
  score?: number | null;
  /**
   * Whether to show the score badge.
   *
   * @optional
   * @type boolean
   * @default true
   */
  showScoreBar?: boolean;
  /**
   * Slot badge to display in the top-left corner of the asset.
   *
   * @optional
   * @type string
   * @default null
   */
  slotNumber?: string | null;
  /**
   * Distance in pixels from the top of the asset to the hover action buttons,
   * already accounting for a rendered `Asset.Label`.
   *
   * @optional
   * @type number
   * @default 7, or 25 when an `Asset.Label` is rendered
   */
  buttonTopOffset?: number;
  /**
   * Handler called when the context menu is mounted.
   *
   * @optional
   * @type (isMounted: boolean) => void
   */
  onContextMenuMount?: (isMounted: boolean) => void;
  /**
   * Handler called when the label is mounted.
   *
   * @optional
   * @type (isMounted: boolean) => void
   */
  onLabelMount?: (isMounted: boolean) => void;
  /**
   * Handler called when the asset is hovered.
   */
  onHoverChange?: (isHovered: boolean) => void;
}

const AssetContext = React.createContext<AssetContextValue>({});

export { AssetContext };
